import { getUserDisplayName } from '@/utils/userDisplayName';
import { LogoutOutlined, SettingOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { history, useModel, useIntl } from '@umijs/max';
import { Spin, Avatar } from 'antd';
import type { MenuProps } from 'antd';
import { createStyles } from 'antd-style';
import { stringify } from 'querystring';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import HeaderDropdown from '../HeaderDropdown';
import { logout } from '@/services/user';
import ChangePasswordModal from '../ChangePasswordModal';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{getUserDisplayName(currentUser)}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
    account: {
      marginLeft: 'auto',
    },
    avatar: {
      marginRight: 8,
    },
    name: {
      fontWeight: 600,
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ menu }) => {
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const intl = useIntl();

  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    try {
      // Call logout API
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // 3. Clear all caches on logout (as required)
      console.log('[Logout] Clearing all permission and role-permission caches');

      // Dispatch logout event for prefetch service
      window.dispatchEvent(new CustomEvent('user-logout'));

      // Clear permission config cache
      const { permissionPrefetchService } = await import('@/services/permission/prefetchService');
      permissionPrefetchService.clearPermissionConfig();

      // Clear all permission and role-permission related cache
      const { permissionStorageService } = await import('@/services/permission/storageService');
      permissionStorageService.clearPermissions();

      // Clear additional caches
      localStorage.removeItem('role_permissions_cache');
      localStorage.removeItem('permission_roles_cache');
      localStorage.removeItem('emergency_permissions');

      // Reset permission store
      const { usePermissionStore } = await import('@/stores/permissionStore');
      usePermissionStore.getState().reset();

      // Clear authentication tokens
      localStorage.removeItem('token');
      localStorage.removeItem('autoLogin');

      // Clear auth manager data (this will also trigger permission clearing)
      const { authManager, AuthSystem } = await import('@/services/auth/authManager');
      authManager.clearAuth(AuthSystem.ADMIN);

      console.log('[Logout] All caches cleared successfully');

      // Note: There may be security issues, please note
      if (window.location.pathname !== '/user/login') {
        history.replace({
          pathname: '/user/login',
          search: stringify({
            redirect: window.location.pathname + window.location.search,
          }),
        });
      }
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === 'changePassword') {
      setChangePasswordVisible(true);
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            icon: <UserOutlined />,
            label: intl.formatMessage({
              id: 'component.globalHeader.user.personalCenter',
              defaultMessage: '個人中心',
            }),
          },
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: intl.formatMessage({
              id: 'component.globalHeader.user.personalSettings',
              defaultMessage: '個人設置',
            }),
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'changePassword',
      icon: <LockOutlined />,
      label: intl.formatMessage({
        id: 'component.globalHeader.user.changePassword',
        defaultMessage: '更改密碼',
      }),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: intl.formatMessage({
        id: 'component.globalHeader.user.logout',
        defaultMessage: '退出登錄',
      }),
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        <span className={`${styles.action} ${styles.account}`}>
          <Avatar
            size="small"
            className={styles.avatar}
            src={currentUser.avatar}
            icon={!currentUser.avatar ? <UserOutlined /> : undefined}
            alt="avatar"
          />
          <span className={`${styles.name} anticon`}>
            {getUserDisplayName(currentUser, '')}
          </span>
        </span>
      </HeaderDropdown>
      <ChangePasswordModal
        visible={changePasswordVisible}
        onCancel={() => setChangePasswordVisible(false)}
      />
    </>
  );
};
